//service.js
import axios from "axios";
import { PublicClientApplication, InteractionRequiredAuthError } from "@azure/msal-browser";
import { msalConfig,loginRequest,scopeRequest } from "../auth/msal/MsalAuthConfig";
import { findUserInputType } from "./helper";

const baseURL = process.env.REACT_APP_ENDPOINT;

const msalInstance = new PublicClientApplication(msalConfig);

await msalInstance.initialize();                 

const basePeoplePickerURL = process.env.PEOPLE_PICKER_API;

const APP_LOGIN_REDIRECT = "/";

const authRedirectToLogin = async () => {
  const account =
    msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0] || null;

  try {
    msalInstance.setActiveAccount(null);
    await msalInstance.clearCache(account ? { account } : undefined);
  } catch (e) {
    console.error("Failed to clear MSAL cache before redirecting to login:", e);
  } finally {
    window.location.replace(APP_LOGIN_REDIRECT);
  }
};

let tokenCache = null;   

async function getAccessToken () {
  const now = Date.now() / 1000;                       // seconds
  if (tokenCache && tokenCache.exp - 60 > now) {
    return tokenCache.token;                            // still fresh
  }

  const account =
    msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
  if (!account) throw new Error("No signed-in MSAL account – login first.");

  try {
    const res = await msalInstance.acquireTokenSilent({ ...scopeRequest, account });
    tokenCache = { token: res.accessToken, exp: res.expiresOn.getTime() / 1000 };
    return res.accessToken;
  } catch (err) {
  // Any interaction-required/refresh-token issue → try one forced silent, then bounce to login.
    const looksInteractionRequired =
      err instanceof InteractionRequiredAuthError ||
      String(err?.errorCode || "").includes("interaction") ||
      /refresh token|sign-in frequency|invalid_grant/i.test(String(err?.errorMessage || err));

    if (looksInteractionRequired) {
      try {
        const res = await msalInstance.acquireTokenSilent({
          ...scopeRequest,
          account,
          forceRefresh: true,
        });
        tokenCache = { token: res.accessToken, exp: res.expiresOn.getTime() / 1000 };
        return res.accessToken;
      } catch {
        // Force the user through a clean re-auth
        await authRedirectToLogin(); // does not return
      }
    }
    // Unknown error: still bounce to be safe
    await authRedirectToLogin();
    throw err; // in case redirect is blocked
  }
}



const getPeopleAccessToken = async () => {
  const accounts = msalInstance.getAllAccounts();
  const account = accounts[0];
  if (!accounts[0]) throw new Error("No active account found. Please log in.");
  try {
    const response = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account
    });
    return response.accessToken;
  } catch (error) {
    console.error("Error acquiring People token:", error);
    await authRedirectToLogin();
    throw error;
  }
};


export const getRequestPeoplePicker = async (subPath) => {
  const url = `${basePeoplePickerURL}/${subPath}`;
  const accessToken = await getPeopleAccessToken();
  const config = {
    headers: {
      // 'Access-Control-Allow-Origin': '*',
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
      ConsistencyLevel : "eventual"
    },
  };
  const response = await axios.get(url, config);
  return response.data;
};

const makeRequest = async (method, endPoint, data = null, requestConfig = {}) => {
  const url = `${baseURL}${endPoint}`;
  const accessToken = await getAccessToken();
  const config = {
    ...requestConfig,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
      ...(requestConfig.headers || {}),
    },
  };
  try {
    const response = await axios({ method, url, data, ...config });
    return requestConfig.returnResponse ? response : response.data;
  } catch (error) {
    console.error(`Error making ${method} request:`, error);
    const status = error?.response?.status;

    if (status === 401 || status === 403) {
       console.log("Error is at server side", error); 
      // await authRedirectToLogin();
    } 
    else if (status >= 500) {
      // window.location.href = '/error'; // Hard redirect to your new error page
      console.log("Error is at server side", error);
    }

    throw error;
  }
};

export const postRequest = (endPoint, data) => makeRequest("post", endPoint, data);
export const postBlobRequest = (endPoint, data) =>
  makeRequest("post", endPoint, data, { responseType: "blob", returnResponse: true });
export const getRequest = (endPoint) => makeRequest("get", endPoint);
export const putRequest = (endPoint, data) => makeRequest("put", endPoint, data);
export const deleteRequest = (endPoint) => makeRequest("delete", endPoint);

export const fetchUsersByInput = async (request) => {
  const mode = findUserInputType(request);
  let url = "";
  if (mode === "email") {
    url = `?$filter=startswith(mail,'${request}')&$select=displayName,givenName,surname,mail,onPremisessAMaccountname&$count=true&$top=10`
  } else if (mode === "lillyId") {
    url = `?$filter=startswith(onPremisessAMaccountname,'${request}')&$select=displayName,givenName,surname,mail,onPremisessAMaccountname&$count=true&$top=10`;
  } else if (mode === "name") {
    // Break down for surname
    const fullName = request.split(" ");
    const cleanedNameArr = fullName.filter((element) => element !== undefined && element !== null && element !== "");
    if (cleanedNameArr.length === 1) {
      url = `?$filter=startswith(givenName,'${cleanedNameArr[0]}') or startswith(surname,'${cleanedNameArr[0]}') &$select=displayName,givenName,surname,mail,onPremisessAMaccountname&$count=true&$top=20`;
    } else if (cleanedNameArr.length === 2) {
      url = `?$filter=startswith(givenName,'${cleanedNameArr[0]}') and startswith(surname,'${cleanedNameArr[1]}')&$select=displayName,givenName,surname,mail,onPremisessAMaccountname&$count=true&$top=10`;
    } else {
      url = `?$filter=startswith(displayName,'${request.query}')&$select=displayName,givenName,surname,mail,onPremisessAMaccountname&$count=true&$top=10`;
    }
  }
  const response = await getRequestPeoplePicker(url);

  const filteredUsers = response.value.filter(user => {
    const sam = user?.onPremisesSamAccountName?.toUpperCase() || "";
    // Return *only* those that do NOT contain 'CA' or 'DS'
    return !sam.includes("CA") && !sam.includes("DS");
  });
  return { ...response, value: filteredUsers };
};
